import { useState, useEffect } from 'react';
import { Table, Card, ScrollArea, LoadingOverlay, Box } from '@mantine/core';
import { SortableTableHeader, NonSortableColumnHeader } from '../../common/SortableTableHeader';
import { ColumnAveragesRow } from '../../common/ConfigurableTable';
import { CollapsibleGroupHeader } from '../components/CollapsibleGroupHeader';
import { Pagination } from '../../common/Pagination';
import type { SortConfig, ColumnConfig } from '../../../hooks/useColumnConfig';
import type { DataLibrarySurveyRow, DataLibrarySurveyQuestion, ColumnAverages } from '../../../types';
import { flattenGroups, type GroupNode } from '../../../utils/groupDataByField';
import { renderSurveyCell } from './SurveyTabCellRenderers';
import classes from '../../common/ConfigurableTable/ConfigurableTable.module.css';
import { ResizableHeaderCell } from '../components/ResizableHeaderCell';
import { useDataLibraryColumnWidths } from '../useDataLibraryTableLayout';
import { ColumnWidthCell } from '../components/ColumnWidthCell';
import { DataLibraryEmptyState } from '../components/DataLibraryEmptyState';

interface SurveyTabTableProps {
  data: DataLibrarySurveyRow[];
  groupedData: GroupNode<DataLibrarySurveyRow>[] | null;
  columns: ColumnConfig[];
  questions: DataLibrarySurveyQuestion[];
  sortConfig: SortConfig | null;
  onSort: (key: string) => void;
  loading: boolean;
  hasActiveFilters: boolean;
  onClearFilters: () => void;
  emptyMessage: string;
  isMidVsEnd?: boolean;
  // Pagination
  page: number;
  pageSize: number;
  totalCount: number;
  /** MDF-41: server averages over all matching rows */
  averages: ColumnAverages;
  totalPages: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
  /** Hide pagination (e.g. when grouped and not mid-vs-end) */
  hidePagination?: boolean;
}

// Columns whose cell has its own Tooltip get no title. Add a new one here, or a cut cell shows two boxes.
const TOOLTIP_COLUMNS = ['email', 'attendance_rate', 'has_open_flag'];

function getColStyle(colId: string, questions: DataLibrarySurveyQuestion[]): React.CSSProperties {
  if (colId.startsWith('q_')) {
    const questionId = colId.slice(2);
    const question = questions.find(q => q.question_id === questionId);
    if (question?.question_type === 'text') {
      return { minWidth: 400, whiteSpace: 'normal', overflow: 'visible', verticalAlign: 'top' };
    }
  }
  return { minWidth: 200, whiteSpace: 'nowrap', overflow: 'hidden' };
}

function SurveyRow({
  widths,
  row,
  columns,
  questions,
}: {
  widths: Record<string, number>;
  row: DataLibrarySurveyRow;
  columns: ColumnConfig[];
  questions: DataLibrarySurveyQuestion[];
}) {
  return (
    <Table.Tr className={classes.tableRow}>
      {columns.map((col, index) => (
        <ColumnWidthCell
          key={col.id}
          className={index === 0 ? classes.stickyColumn : undefined}
          style={getColStyle(col.id, questions)}
          width={widths[col.id]}
          hasTooltip={TOOLTIP_COLUMNS.includes(col.id)}
        >
          {renderSurveyCell(row, col.id, questions)}
        </ColumnWidthCell>
      ))}
    </Table.Tr>
  );
}


export function SurveyTabTable({
  data,
  groupedData,
  columns,
  questions,
  sortConfig,
  onSort,
  loading,
  hasActiveFilters,
  onClearFilters,
  emptyMessage,
  page,
  pageSize,
  totalCount,
  averages,
  totalPages,
  onPageChange,
  onPageSizeChange,
  hidePagination = false,
}: SurveyTabTableProps) {
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set());
  const layout = useDataLibraryColumnWidths();

  const toggleGroup = (key: string) => {
    setCollapsedGroups(prev => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  // Reset collapsed state when group keys change (AC21)
  const groupKeys = groupedData ? groupedData.map(g => g.key).join('\0') : '';
  useEffect(() => {
    setCollapsedGroups(new Set());
  }, [groupKeys]);

  const averagesRow = (
    <ColumnAveragesRow
      columns={columns}
      averages={averages}
      rows={data}
      totalCount={totalCount}
      firstCellClassName={classes.stickyColumn}
    />
  );

  const hasData = data.length > 0 || (groupedData && groupedData.length > 0);
  const showEmpty = groupedData ? groupedData.length === 0 : !loading && data.length === 0;

  const tableHeader = (
    <Table.Thead className={classes.tableHeader}>
      <Table.Tr>
        {columns.map((col, index) =>
          col.sortable ? (
            <ResizableHeaderCell
              key={col.id}
              column={col}
              sticky={index === 0}
              style={getColStyle(col.id, questions)}
              layout={layout}
            >
              <SortableTableHeader
                label={col.label}
                sortKey={col.id}
                currentSort={sortConfig}
                onSort={onSort}
              />
            </ResizableHeaderCell>
          ) : (
            <ResizableHeaderCell
              key={col.id}
              column={col}
              sticky={index === 0}
              style={getColStyle(col.id, questions)}
              layout={layout}
            >
              <NonSortableColumnHeader label={col.label} />
            </ResizableHeaderCell>
          ),
        )}
      </Table.Tr>
    </Table.Thead>
  );

  const showPagination = !hidePagination && totalCount > 0 && (!loading || !!hasData);

  // Grouped view — collapsible sections
  if (groupedData) {
    return (
      <Card
        withBorder
        p={0}
        radius={8}
        bg="white"
        style={{ boxShadow: 'none', position: 'relative' }}
        className={classes.tableCard}
      >
        <LoadingOverlay visible={loading && !!hasData} overlayProps={{ blur: 1 }} />
        <ScrollArea scrollbarSize={8} type="auto" offsetScrollbars={false}>
          <Table highlightOnHover className={classes.table}>
            {tableHeader}
            <Table.Tbody>
              {averagesRow}
              {flattenGroups(groupedData, collapsedGroups).map(item =>
                'group' in item ? (
                  <CollapsibleGroupHeader
                    key={`group-${item.group.key}`}
                    label={item.group.label}
                    count={item.group.rows.length}
                    depth={item.group.depth}
                    colSpan={columns.length}
                      columns={columns}
                      rows={item.group.rows}
                    isCollapsed={collapsedGroups.has(item.group.key)}
                    onToggle={() => toggleGroup(item.group.key)}
                  />
                ) : (
                  <SurveyRow
                    widths={layout.columnWidths}
                    key={item.row.submission_id}
                    row={item.row}
                    columns={columns}
                    questions={questions}
                  />
                ),
              )}
            </Table.Tbody>
          </Table>
        </ScrollArea>
        {showEmpty && <DataLibraryEmptyState message={emptyMessage} hasActiveFilters={hasActiveFilters} onClearFilters={onClearFilters} />}
        {showPagination && (
          <Box className={classes.paginationWrapper}>
            <Pagination
              page={page}
              pageSize={pageSize}
              total={totalCount}
              totalPages={totalPages}
              onPageChange={onPageChange}
              onPageSizeChange={onPageSizeChange}
              pageSizeOptions={[25, 50, 100, 250]}
              entityLabel="responses"
            />
          </Box>
        )}
      </Card>
    );
  }

  // Flat view
  return (
    <Card
      withBorder
      p={0}
      radius={8}
      bg="white"
      style={{ boxShadow: 'none', position: 'relative' }}
      className={classes.tableCard}
    >
      <LoadingOverlay visible={loading && !!hasData} overlayProps={{ blur: 1 }} />
      <ScrollArea scrollbarSize={8} type="auto" offsetScrollbars={false}>
        <Table highlightOnHover className={classes.table}>
          {tableHeader}
          <Table.Tbody>
            {averagesRow}
            {data.map(row => (
              <SurveyRow
                widths={layout.columnWidths}
                key={row.submission_id}
                row={row}
                columns={columns}
                questions={questions}
              />
            ))}
          </Table.Tbody>
        </Table>
      </ScrollArea>
      {showEmpty && <DataLibraryEmptyState message={emptyMessage} hasActiveFilters={hasActiveFilters} onClearFilters={onClearFilters} />}
      {showPagination && (
        <Box className={classes.paginationWrapper}>
          <Pagination
            page={page}
            pageSize={pageSize}
            total={totalCount}
            totalPages={totalPages}
            onPageChange={onPageChange}
            onPageSizeChange={onPageSizeChange}
            pageSizeOptions={[25, 50, 100, 250]}
            entityLabel="responses"
          />
        </Box>
      )}
    </Card>
  );
}
